import React, { FormEvent, JSX, useEffect, useState } from 'react';
import cep from 'cep-promise';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { updateMask as updateMaskCPF } from '@/helpers/masks/cpf';
import { updateMask as updateMaskPhone } from '@/helpers/masks/mobilePhone';
import { updateMask as updateMaskCEP } from '@/helpers/masks/cep';
import { unmask, updateMask as updateMaskDate } from '@/helpers/masks/generalDate';
import { getErrorMessage } from '@/helpers/error';

import { setLoading, useLoading } from '@/redux/loading/loadingSlice';
import { api } from '@/services/api';

import { Address } from '@/model/Address';
import ClientBasic from '@/model/ClientBasic';
import Client from '@/model/Client';
import {
  FirstLogin,
  FormInputNameConfirmEmail,
  FormInputNameRegister,
} from '@/features/register/type';

import { RegisterUI } from '@/features/register/screen/ui';
import { Button, InputText } from '@/components';
import { AuthWrapper } from '@/features/core/auth/screens/AuthWrapper';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { loginClient } from '@/helpers/auth';
import { TermsContent } from '@/features/core/auth/components';
import { consumePendingPurchase, getPendingPurchase } from '@/helpers/navigation/pendingPurchase';

enum RegisterFlowStep {
  REGISTER = 'REGISTER',
  CONFIRM_WHATSAPP = 'CONFIRM_WHATSAPP',
  SUCCESS = 'SUCCESS',
}

export const RegisterScreen: React.FC = (): JSX.Element => {
  const [shouldShowPasswordToText, setShouldShowPasswordToText] = useState(false);
  const [shouldShowPasswordToText2, setShouldShowPasswordToText2] = useState(false);
  const [firstLogin, setFirstLogin] = useState<FirstLogin>();
  const [flowStep, setFlowStep] = useState<RegisterFlowStep>(RegisterFlowStep.REGISTER);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const registerState = location.state as { cpf?: string; name?: string } | null;
  const cpfFromState = registerState?.cpf?.trim() || '';
  const nameFromState = registerState?.name?.trim() || '';
  const cpfFromQuery = searchParams.get('cpf')?.trim() || '';
  const cpfInitialValue = cpfFromState || cpfFromQuery;
  const [isTermsVisible, setIsTermsVisible] = useState(false);
  const navigate = useNavigate();
  const pendingPurchase = getPendingPurchase();

  const dispatch = useDispatch();
  const { loading } = useSelector(useLoading);

  const {
    formData: formDataRegister,
    formErrors: formErrorsRegister,
    setErrors: setErrorsRegister,
    onChangeFormInput: onChangeFormInputRegister,
  } = useForm({
    initialData: {
      name: '',
      document: '',
      email: '',
      phone: '',
      date: '',
      motherName: '',
      zipCode: '',
      state: '',
      city: '',
      district: '',
      street: '',
      complement: '',
      addressnumber: '',
      password: '',
      confirmPassword: '',
      terms: '',
    },
    validators: {
      name: [validators.required],
      document: [validators.required, validators.cpf],
      email: [validators.required, validators.email],
      phone: [validators.required, validators.mobilePhone],
      date: [validators.required, validators.birthday, validators.maxLength(10)],
      motherName: [validators.required],
      zipCode: [validators.required],
      state: [validators.required],
      city: [validators.required],
      district: [validators.required],
      street: [validators.required],
      password: [
        validators.required,
        validators.minLength(8),
        validators.maxLength(15),
        validators.hasPasswordOnlyNumberCharacteres,
      ],
      confirmPassword: [
        validators.required,
        validators.minLength(8),
        validators.maxLength(15),
        validators.hasPasswordOnlyNumberCharacteres,
      ],
      terms: [validators.required],
    },
    formatters: {
      document: updateMaskCPF,
      phone: updateMaskPhone,
      date: updateMaskDate,
      zipCode: updateMaskCEP,
    },
  });

  const {
    formData: formDataConfirmEmail,
    formErrors: formErrorsConfirmEmail,
    onChangeFormInput: onChangeFormInputConfirmEmail,
    isFormValid: isFormValidConfirmEmail,
  } = useForm({
    initialData: {
      cpf: '',
      confirmationCode: '',
    },
    validators: {
      cpf: [validators.required, validators.cpf],
      confirmationCode: [validators.required],
    },
    formatters: {
      cpf: updateMaskCPF,
    },
  });

  const handleToggleTerms = (): void => {
    setIsTermsVisible(prev => !prev);
  };

  const openWhatsAppConfirmation = (cpf: string): void => {
    onChangeFormInputConfirmEmail(FormInputNameConfirmEmail.cpf)(cpf);
    setFlowStep(RegisterFlowStep.CONFIRM_WHATSAPP);
  };

  const handleOnConfirmWhatsApp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (!isFormValidConfirmEmail()) return;

    try {
      dispatch(setLoading(true));

      const payload = {
        cpf: formDataConfirmEmail[FormInputNameConfirmEmail.cpf],
        confirmCode: formDataConfirmEmail[FormInputNameConfirmEmail.confirmationCode],
      };

      await api.patch('/client/confirm-whatsapp', payload);
      toast.success('Conta confirmada com sucesso!');
      setFlowStep(RegisterFlowStep.SUCCESS);
    } catch (error) {
      toast.error(getErrorMessage(error));
      return;
    } finally {
      dispatch(setLoading(false));
    }

    if (firstLogin?.cpf && firstLogin?.password) {
      try {
        await loginClient(firstLogin.cpf, firstLogin.password);
      } catch {
        toast.info(
          'Sua conta foi ativada, mas não foi possível entrar automaticamente. Faça o login para continuar.',
        );
      }
    }

    const purchasePath = consumePendingPurchase();
    if (purchasePath) {
      navigate(purchasePath, { replace: true });
    }
  };

  const handleOnTogglePasswordToText = (): void => {
    setShouldShowPasswordToText(prev => !prev);
  };

  const handleOnTogglePasswordToText2 = (): void => {
    setShouldShowPasswordToText2(prev => !prev);
  };

  const handleOnSubmitRegister = async (e: FormEvent): Promise<void> => {
    try {
      e.preventDefault();
      dispatch(setLoading(true));

      const dateString = unmask(formDataRegister[FormInputNameRegister.date]);

      if (!dateString) {
        setErrorsRegister({
          [FormInputNameRegister.date]: ['Data de nascimento inválida!'],
        });
        return;
      }

      if (
        formDataRegister[FormInputNameRegister.password] !==
        formDataRegister[FormInputNameRegister.confirmPassword]
      ) {
        setErrorsRegister({
          [FormInputNameRegister.password]: ['Senhas divergentes!'],
          [FormInputNameRegister.confirmPassword]: ['Senhas divergentes!'],
        });
        return;
      }

      const date = new Date(`${dateString}T12:00:00.000Z`);

      const address = {
        zipCode: formDataRegister[FormInputNameRegister.zipCode],
        state: formDataRegister[FormInputNameRegister.state],
        city: formDataRegister[FormInputNameRegister.city],
        district: formDataRegister[FormInputNameRegister.district],
        street: formDataRegister[FormInputNameRegister.street],
        complement: formDataRegister[FormInputNameRegister.complement],
        number: formDataRegister[FormInputNameRegister.addressnumber],
      } as Address;

      const payload: ClientBasic = {
        name: formDataRegister[FormInputNameRegister.name],
        cpf: formDataRegister[FormInputNameRegister.document],
        email: formDataRegister[FormInputNameRegister.email],
        cellPhone: formDataRegister[FormInputNameRegister.phone],
        birthDate: date,
        motherName: formDataRegister[FormInputNameRegister.motherName],
        password: formDataRegister[FormInputNameRegister.password],
        address,
        acceptedTerms:
          !!formDataRegister[FormInputNameRegister.terms] &&
          formDataRegister[FormInputNameRegister.terms] === 'true',
        confirmationCode: formDataRegister[FormInputNameRegister.confirmationCode],
      };

      const { data } = await api.post<Client>('/client', payload);

      setFirstLogin({
        cpf: data.cpf,
        password: formDataRegister[FormInputNameRegister.password],
      });

      openWhatsAppConfirmation(data.cpf);
    } catch (error) {
      setErrorsRegister({
        [FormInputNameRegister.document]: [
          getErrorMessage(error) || 'Falha ao realizar cadastro, tente novamente mais tarde',
        ],
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnChangeCEP = async (value: string): Promise<void> => {
    if (value.length !== 9) return;
    const cepResponse = await cep(value);
    onChangeFormInputRegister(FormInputNameRegister.state)(cepResponse.state);
    onChangeFormInputRegister(FormInputNameRegister.city)(cepResponse.city);
    onChangeFormInputRegister(FormInputNameRegister.district)(cepResponse.neighborhood);
    onChangeFormInputRegister(FormInputNameRegister.street)(cepResponse.street);
  };

  const handleGoToLogin = (): void => {
    navigate(consumePendingPurchase() || '/');
  };

  useEffect(() => {
    if (cpfInitialValue && formDataRegister[FormInputNameRegister.document] !== cpfInitialValue) {
      onChangeFormInputRegister(FormInputNameRegister.document)(cpfInitialValue);
    }

    if (nameFromState && formDataRegister[FormInputNameRegister.name] !== nameFromState) {
      onChangeFormInputRegister(FormInputNameRegister.name)(nameFromState);
    }
  }, [cpfInitialValue, nameFromState, formDataRegister.document, formDataRegister.name]);

  if (flowStep === RegisterFlowStep.CONFIRM_WHATSAPP) {
    return (
      <AuthWrapper isHomepage={false}>
        <div className="min-h-screen bg-gray-50 py-10 px-4">
          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-gray-900">Confirme seu WhatsApp</h1>
              <p className="mt-2 text-sm text-gray-600">
                Enviamos um código para o número informado no cadastro
              </p>
            </div>

            <div className="mx-auto w-full max-w-2xl rounded-2xl bg-white shadow-sm border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-5">
                <div className="flex items-center justify-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white">
                    2
                  </div>
                  <span className="text-sm font-medium text-red-600">Verificação do WhatsApp</span>
                </div>
              </div>

              <form onSubmit={handleOnConfirmWhatsApp} className="px-6 py-6 md:px-8 md:py-8">
                <div className="grid grid-cols-1 gap-5">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      CPF informado
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {formDataConfirmEmail[FormInputNameConfirmEmail.cpf] || '-'}
                    </p>
                  </div>

                  <div>
                    <InputText
                      name="confirmationCode"
                      label="Código de confirmação"
                      placeholder="Digite o código recebido"
                      value={formDataConfirmEmail[FormInputNameConfirmEmail.confirmationCode]}
                      onChange={e =>
                        onChangeFormInputConfirmEmail(FormInputNameConfirmEmail.confirmationCode)(
                          e.target.value,
                        )
                      }
                      error={
                        formErrorsConfirmEmail.confirmationCode &&
                        formErrorsConfirmEmail.confirmationCode[0]
                      }
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-col md:flex-row gap-3 md:justify-end">
                  <Button
                    type="submit"
                    title="Confirmar conta"
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full md:w-[220px]"
                    disabled={loading}
                  />
                </div>

                <div className="mt-8 border-t border-gray-100 pt-6 text-center">
                  <span className="text-sm text-gray-500">
                    Não recebeu o código? Confira se o número está correto.
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </AuthWrapper>
    );
  }

  if (flowStep === RegisterFlowStep.SUCCESS) {
    return (
      <AuthWrapper isHomepage={false}>
        <div className="min-h-screen bg-gray-50 py-10 px-4">
          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-gray-900">Cadastro concluído</h1>
              <p className="mt-2 text-sm text-gray-600">
                Sua conta foi criada e confirmada com sucesso.
              </p>
            </div>

            <div className="mx-auto w-full max-w-2xl rounded-2xl bg-white shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-8 md:px-8 md:py-10 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-bold">
                  ✓
                </div>

                <h2 className="text-2xl font-bold text-gray-900">Tudo certo!</h2>

                <p className="mt-2 text-sm text-gray-600">
                  Seu cadastro já foi finalizado e seu WhatsApp confirmado.
                </p>

                {firstLogin?.cpf && (
                  <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      CPF cadastrado
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {updateMaskCPF(firstLogin.cpf)}
                    </p>
                  </div>
                )}

                <div className="mt-8 flex justify-center">
                  <Button
                    type="button"
                    title={pendingPurchase ? 'Continuar compra' : 'Ir para o login'}
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full md:w-[220px]"
                    onClick={handleGoToLogin}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AuthWrapper>
    );
  }
  return (
    <>
      <AuthWrapper isHomepage={false}>
        <RegisterUI
          state={loading}
          formData={formDataRegister}
          formErrors={formErrorsRegister}
          onChangeFormInput={onChangeFormInputRegister}
          onChangeCEP={handleOnChangeCEP}
          shouldShowPasswordToText={shouldShowPasswordToText}
          shouldShowPasswordToText2={shouldShowPasswordToText2}
          onTogglePasswordToText={handleOnTogglePasswordToText}
          onTogglePasswordToText2={handleOnTogglePasswordToText2}
          onSubmit={handleOnSubmitRegister}
          onToggleTerms={handleToggleTerms}
          onGoToLogin={handleGoToLogin}
          isTermsVisible={isTermsVisible}
          termsContent={<TermsContent />}
        />
      </AuthWrapper>
    </>
  );
};
